import React, { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import { HelpCenter } from './HelpCenter';
import { Footer } from './Footer';
import { IntroductionTour } from 'components/common/introductionTour/IntroductionTour';
import { ServiceboardHeader } from './header/ServiceboardHeader';
import { ErrorBoundary } from 'components/common/ErrorBoundary';
import { RoutePaths } from 'routes/RoutePaths';
import { LanguageEnFrOnly } from 'components/hooks/LanguageProvider';
import { getBoardServices } from 'store/ServiceBoardService/serviceBoardServiceThunk';
import { AccessType } from 'models/accessType';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/store';
import { useMatch } from 'react-router';
import classNames from 'classnames';

interface IProps {
    children: React.ReactNode;
}

// Hook pour charger les services du ServiceBoard
const useServiceBoardInit = ({ isServiceBoardRoute }: { isServiceBoardRoute: boolean }) => {
    const dispatch = useAppDispatch();
    const { i18n: { language } } = useTranslation();

    useEffect(() => {
        if (isServiceBoardRoute) {
            dispatch(getBoardServices(AccessType.All, language));
        }
    }, [dispatch, language, isServiceBoardRoute]);
};

const HeaderAndLanguage: React.FC = () => {
    const isRouteHome = !!useMatch(RoutePaths.Home.route);
    const isRouteMyServices = !!useMatch(RoutePaths.MyServices.route + '/*');
    const isRouteCatalog = !!useMatch(RoutePaths.Catalog.route + '/*');
    const isRouteSearch = !!useMatch(RoutePaths.Search.route);
    const isRouteDisclaimer = !!useMatch(RoutePaths.Disclaimer.route);

    const isServiceBoardRoute = isRouteHome
        || isRouteMyServices
        || isRouteCatalog
        || isRouteSearch;

    const needsLanguageEnFrOnly = isRouteCatalog || isRouteDisclaimer;

    useServiceBoardInit({ isServiceBoardRoute });

    return <>
        {needsLanguageEnFrOnly && <LanguageEnFrOnly />}
        <ServiceboardHeader />
    </>;
};

export const Layout: React.FC<IProps> = ({ children }) => {
    const isMyAccount = useMatch(RoutePaths.MyAccount.route);
    const isMyNotification = useMatch(RoutePaths.MyNotification.route);
    const needsFlexGrow = isMyAccount || isMyNotification;

    return <div>
        <HeaderAndLanguage />
        <main
            className={classNames('bg-lvl1 position-relative', {
                'flex-grow-1 d-flex flex-column': needsFlexGrow,
            })}
            style={{ minHeight: 'calc(100vh - var(--header-height))' }}
        >
            <ErrorBoundary>{children}</ErrorBoundary>
        </main>
        <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            closeButton={false}
            icon={false}
            newestOnTop
            closeOnClick
            pauseOnFocusLoss
            draggable
            pauseOnHover
            stacked={true}
        />
        <Footer />
        <HelpCenter />
        <IntroductionTour />
    </div>;
};
